import React, { useContext, useState, useEffect } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
  makeStyles,
  Theme,
  Grid,
} from '@material-ui/core';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { TbPencil } from 'react-icons/tb';

import { DeviceContext } from '@vyce/core/src/contexts/deviceContext';
import { Address } from '@vyce/core/src/types';
import { MAIN_CONTAINER_ID } from '@vyce/core/src/constants';
import { NotificationContext } from '../../../contexts/notificationContext';
import { AppIconButton } from '../../../components/AppIconButton';
import { ButtonTitleWithLoading } from '../../../components/ButtonTitleWithLoading';
import { LocationField } from '../../../components/controlled-inputs';
import { AppTextField } from '../../../components/inputs/AppTextField';
import { changeUserAddressRequest } from '../../../api/legend/users';
import { changeAddressRequest } from '../../../api/users';
import { getLocationDetails } from '../../../utils/location';

const emptyRules = {};

interface Props {
  address?: Address;
  isLegend?: boolean;
  userId: string;
}

interface Form {
  address: Address;
}

export const useStyles = makeStyles((theme: Theme) => ({
  dialogContent: {
    overflowY: 'unset',
    marginTop: 16,
  },
}));

export const EditAddress: React.FC<Props> = ({ address: outerAddress, isLegend, userId }) => {
  const { handleServerError } = useContext(NotificationContext);
  const { isMobile } = useContext(DeviceContext);
  const theme = useTheme();
  const classes = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const [address, setAddress] = useState<Address>(
    outerAddress ?? {
      address_line_1: '',
      address_line_2: '',
      address_line_3: '',
      address_line_4: '',
      country: '',
      address_snippet: '',
      lat: 0,
      lon: 0,
    }
  );
  const [loading, setLoading] = useState<boolean>(false);
  const methods = useForm<Form>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      address: outerAddress,
    },
  });
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = methods;
  const fullAddress = watch('address.address_snippet');

  const onSubmit = async (data: Form, event?: React.BaseSyntheticEvent<object, any, any>) => {
    event?.preventDefault();
    const newAddress = {
      address_line_1: data.address.address_line_1,
      address_line_2: data.address.address_line_2,
      address_line_3: data.address.address_line_3,
      address_line_4: data.address.address_line_4,
      country: data.address.country,
      address_snippet: data.address.address_snippet,
      lat: data.address.lat,
      lon: data.address.lon,
    };
    try {
      setLoading(true);
      if (isLegend) {
        await changeUserAddressRequest(newAddress, userId);
      } else {
        await changeAddressRequest(newAddress);
      }
      setAddress(data.address);
      setOpen(false);
      return false;
    } catch (e) {
      handleServerError(e);
    } finally {
      setLoading(false);
    }
  };

  const checkAddress = async (fullAddress: string) => {
    const { address_line_4, address_line_2, address_line_3, address_line_1, lat, lon } =
      await getLocationDetails({
        fullAddress,
      });
    setValue('address.address_line_2', address_line_2 || '');
    setValue('address.address_line_1', address_line_1 || '');
    setValue('address.address_line_3', address_line_3 || '');
    setValue('address.address_line_4', address_line_4 || '');
    setValue('address.lat', lat || 0);
    setValue('address.lon', lon || 0);
  };

  useEffect(() => {
    if (fullAddress && address?.address_snippet !== fullAddress) {
      checkAddress(fullAddress);
    }
  }, [fullAddress]);

  return (
    <>
      <Box display="flex" height="100%" alignItems="center" justifyContent="space-between">
        <Box>
          <Box display="flex">
            <Typography variant="subtitle2" color="textSecondary">
              Address{' '}
            </Typography>
            <Typography
              style={{
                color: theme.palette.text.secondary,
                marginLeft: 4,
              }}
              variant="subtitle2"
            ></Typography>
            <Typography variant="subtitle2">:</Typography>
          </Box>
          <Typography variant="caption" style={{ fontWeight: 500 }}>
            {address.address_snippet}
          </Typography>
        </Box>

        <AppIconButton variant="primary" onClick={() => setOpen(true)}>
          <TbPencil color={theme.palette.primary.dark} size="18px" />
        </AppIconButton>
      </Box>

      <Dialog
        container={document.getElementById(MAIN_CONTAINER_ID)}
        fullScreen={isMobile}
        open={open}
        onClose={() => setOpen(false)}
      >
        <FormProvider {...methods}>
          <form>
            <DialogTitle>Update my Address</DialogTitle>
            <DialogContent className={classes.dialogContent}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <LocationField rules={emptyRules} name="address.address_snippet" />
                </Grid>

                <Grid item xs={12} lg={6}>
                  <Controller
                    control={control}
                    name="address.address_line_4"
                    render={({ field }) => (
                      <AppTextField
                        {...field}
                        value={field.value || ''}
                        id="postCode"
                        label="Postcode"
                        error={!!errors?.address?.address_line_4?.message}
                        helperText={errors?.address?.address_line_4?.message || ''}
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} lg={6}>
                  <Controller
                    control={control}
                    name="address.address_line_2"
                    render={({ field }) => (
                      <AppTextField
                        {...field}
                        value={field.value || ''}
                        id="street"
                        label="Street"
                        error={!!errors?.address?.address_line_2?.message}
                        helperText={errors?.address?.address_line_2?.message || ''}
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} lg={6}>
                  <Controller
                    control={control}
                    name="address.address_line_1"
                    render={({ field }) => (
                      <AppTextField
                        {...field}
                        error={!!errors?.address?.address_line_1?.message}
                        helperText={errors?.address?.address_line_1?.message || ''}
                        value={field.value || ''}
                        id="house_number"
                        label="House Number"
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} lg={6}>
                  <Controller
                    control={control}
                    name="address.address_line_3"
                    render={({ field }) => (
                      <AppTextField
                        error={!!errors?.address?.address_line_3?.message}
                        helperText={errors?.address?.address_line_3?.message || ''}
                        {...field}
                        value={field.value || ''}
                        id="town"
                        label="Town"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button size="large" variant="outlined" onClick={() => setOpen(false)}>
                Cancel
              </Button>

              <Button
                size="large"
                type="button"
                style={{ width: 100 }}
                disabled={loading}
                color="primary"
                variant="contained"
                onClick={() => onSubmit(methods.getValues())}
              >
                <ButtonTitleWithLoading title="Save" loaderVariant="paper" loading={loading} />
              </Button>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
    </>
  );
};
